import React, { useEffect, useState } from "react";
import {
  Grid,
  Box,
  TextField,
  MenuItem,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  Button,
  IconButton,
  Typography,
  Alert,
  Avatar,
  Card,
  CardContent,
} from "@mui/material";
import AddBoxIcon from "@mui/icons-material/AddBox";
import DeleteIcon from "@mui/icons-material/Delete";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { getRequestGlobal } from "../../../API/global";

const TeamInfo = ({ formData, setFormData, errors, setTeamInfoErrors }) => {
  const [countries, setCountries] = useState({});
  const [states, setStates] = useState({});
  const [cities, setCities] = useState({});

  const MAX_TEAM_MEMBERS = 4;
  const MAX_FILE_SIZE = 1 * 1024 * 1024; // 1MB in bytes

  // Safely get team members array
  const getTeamMembers = () => {
    if (Array.isArray(formData?.team_members)) {
      return formData.team_members;
    }

    if (typeof formData?.team_members === 'string') {
      try {
        const parsed = JSON.parse(formData.team_members);
        setFormData({
          ...formData,
          team_members: Array.isArray(parsed) ? parsed : []
        });
        return Array.isArray(parsed) ? parsed : [];
      } catch (e) {
        console.error('Error parsing team_members:', e);
        return [];
      }
    }

    return [];
  };

  const teamMembers = getTeamMembers();

  // Automatically add one empty team member when array is empty
  useEffect(() => {
    if (teamMembers.length === 0) {
      const newMember = {
        team_members_name: '',
        team_members_email: '',
        team_members_role: ''
      };

      const updatedMembers = [newMember];
      setFormData({
        ...formData,
        team_members: updatedMembers
      });
    }
  }, [teamMembers.length]);

  const canAddMore = teamMembers.length < MAX_TEAM_MEMBERS;

  // Handle main form changes
  const handleTeamInfoChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name]) {
      setTeamInfoErrors((prev) => ({ ...prev, [name]: "" }));
    }

    if (name === "permanent_state") {
      loadPermanentCity(value);
    }
  };

  // Handle team leader photo upload
  const handleTeamLeaderPhotoUpload = (event) => {
    const file = event.target.files[0];

    if (!file) return;

    // Check file size
    if (file.size > MAX_FILE_SIZE) {
      alert("File size must be less than 1MB");
      return;
    }

    // Check file type
    if (!file.type.startsWith('image/')) {
      alert("Please upload an image file");
      return;
    }

    const reader = new FileReader();

    reader.onload = (e) => {
      const base64 = e.target.result;
      setFormData((prev) => ({
        ...prev,
        teamleaderphoto: base64
      }));

      // Clear any existing photo errors
      if (errors.teamleaderphoto) {
        setTeamInfoErrors((prev) => ({ ...prev, teamleaderphoto: "" }));
      }
    };

    reader.onerror = () => {
      alert("Error reading file. Please try again.");
    };

    reader.readAsDataURL(file);
  };

  // Remove team leader photo
  const handleRemoveTeamLeaderPhoto = () => {
    setFormData((prev) => ({
      ...prev,
      teamleaderphoto: ""
    }));
  };

  // Add new team member
  const handleAddMember = () => {
    if (!canAddMore) return;

    const newMember = {
      team_members_name: '',
      team_members_email: '',
      team_members_role: ''
    };

    const updatedMembers = [...teamMembers, newMember];
    setFormData({
      ...formData,
      team_members: updatedMembers
    });
  };

  // Remove team member
  const handleRemoveMember = (index) => {
    const updatedMembers = teamMembers.filter((_, i) => i !== index);
    setFormData({
      ...formData,
      team_members: updatedMembers
    });

    // Clear errors for removed member
    if (errors && setTeamInfoErrors) {
      const newErrors = { ...errors };
      delete newErrors[`team_members_${index}_name`];
      delete newErrors[`team_members_${index}_email`];
      delete newErrors[`team_members_${index}_role`];
      setTeamInfoErrors(newErrors);
    }
  };

  // Handle team member field changes
  const handleMemberChange = (index, field, value) => {
    const updatedMembers = teamMembers.map((member, i) =>
      i === index ? { ...member, [field]: value } : member
    );

    setFormData({
      ...formData,
      team_members: updatedMembers
    });

    // Clear error for this field when user starts typing
    if (errors && setTeamInfoErrors && errors[`team_members_${index}_${field}`]) {
      const newErrors = { ...errors };
      delete newErrors[`team_members_${index}_${field}`];
      setTeamInfoErrors(newErrors);
    }
    setTeamInfoErrors({})
  };

  // Load cities for state
  const loadPermanentCity = async (state_id) => {
    try {
      const data = await getRequestGlobal("city", { state_id });
      setCities(data.data || {});
    } catch (error) {
      console.error("City fetch error:", error);
    }
  };

  // Load initial data
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const [countryRes, stateRes] = await Promise.all([
          getRequestGlobal("country", {}),
          getRequestGlobal("state", {}),
        ]);
        setCountries(countryRes.data || {});
        setStates(stateRes.data || {});
        if (formData.permanent_state) {
          loadPermanentCity(formData.permanent_state);
        }
      } catch (error) {
        console.error("Failed to load country/state:", error);
      }
    };

    loadInitialData();
  }, [formData.permanent_state]);

  return (
    <>
      <div className="cmot-phone">
        <h5>STEP 2</h5>
        <p>
          Team Information <span className="text-danger">*</span>
        </p>
      </div>

      {!canAddMore && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Maximum {MAX_TEAM_MEMBERS} team members allowed.
        </Alert>
      )}

      <form>
        <Grid container spacing={2} className="mt-3">
          {/* Team Basic Information */}
          <Grid item xs={12} sm={12} md={6} lg={6}>
            <TextField
              variant="outlined"
              label="Team Name"
              type="text"
              fullWidth
              name="team_name"
              value={formData.team_name || ""}
              onChange={handleTeamInfoChange}
              error={!!errors.team_name}
              helperText={errors.team_name}
            />
          </Grid>


          <Grid item xs={12} sm={12} md={6}>
            <TextField
              variant="outlined"
              label="Team Leader (Primary Contact)"
              type="text"
              fullWidth
              name="team_leader"
              value={formData.team_leader || ""}
              onChange={handleTeamInfoChange}
              error={!!errors.team_leader}
              helperText={errors.team_leader}
            />
          </Grid>

          <Grid item xs={12} sm={12} md={6}>
            <TextField
              variant="outlined"
              label="Email Address"
              type="email"
              fullWidth
              name="email"
              value={formData.email || ""}
              onChange={handleTeamInfoChange}
              error={!!errors.email}
              helperText={errors.email}
            />
          </Grid>

          <Grid item xs={12} sm={12} md={6}>
            <TextField
              variant="outlined"
              label="Phone Number with country code"
              type="text"
              fullWidth
              name="phone"
              value={formData.phone || ""}
              onChange={handleTeamInfoChange}
              error={!!errors.phone}
              helperText={errors.phone}
            />
          </Grid>

          <Grid item xs={12} sm={12} md={6}>
            <TextField
              variant="outlined"
              label="Institution / Organization (if applicable)"
              type="text"
              fullWidth
              name="organization"
              value={formData.organization || ""}
              onChange={handleTeamInfoChange}
              error={!!errors.organization}
              helperText={errors.organization}
            />
          </Grid>

          <Grid item xs={12} sm={12} md={6}>
            <Box>
              <TextField
                id="address"
                label="Postal Address"
                multiline
                fullWidth
                name="address"
                value={formData.address || ""}
                onChange={handleTeamInfoChange}
              />
            </Box>
            {errors.address && <p className="error-msg">{errors.address}</p>}
          </Grid>

          <Grid item xs={12} sm={12} md={6}>
            <Box>
              <TextField
                id="zipcode"
                label="zipcode"
                multiline
                fullWidth
                name="zipcode"
                value={formData.zipcode || ""}
                onChange={handleTeamInfoChange}
              />
            </Box>
            {errors.zipcode && <p className="error-msg">{errors.zipcode}</p>}
          </Grid>

          {/* Country Selection */}
          <Grid item xs={12} md={6}>
            <TextField
              select
              fullWidth
              label="Country"
              name="country"
              value={formData.country || ""}
              onChange={handleTeamInfoChange}
              error={!!errors.country}
              helperText={errors.country}
            >
              <MenuItem value="">Select Country</MenuItem>
              {Object.entries(countries)
                .sort((a, b) => a[1].localeCompare(b[1]))
                .map(([id, name]) => (
                  <MenuItem key={id} value={id}>
                    {name}
                  </MenuItem>
                ))}
            </TextField>
          </Grid>

          {/* State & City (India Only) */}
          {formData.country === "101" && (
            <>
              <Grid item xs={12} md={6}>
                <TextField
                  select
                  fullWidth
                  label="State"
                  name="permanent_state"
                  value={formData.permanent_state || ""}
                  onChange={handleTeamInfoChange}
                  error={!!errors.permanent_state}
                  helperText={errors.permanent_state}
                >
                  <MenuItem value="">Select State</MenuItem>
                  {Object.entries(states)
                    .sort((a, b) => a[1].localeCompare(b[1]))
                    .map(([id, name]) => (
                      <MenuItem key={id} value={id}>
                        {name}
                      </MenuItem>
                    ))}
                </TextField>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  select
                  fullWidth
                  label="City"
                  name="permanent_city"
                  value={formData.permanent_city || ""}
                  onChange={handleTeamInfoChange}
                  error={!!errors.permanent_city}
                  helperText={errors.permanent_city}
                >
                  <MenuItem value="">Select City</MenuItem>
                  {Object.entries(cities)
                    .sort((a, b) => a[1].localeCompare(b[1]))
                    .map(([id, name]) => (
                      <MenuItem key={id} value={id}>
                        {name}
                      </MenuItem>
                    ))}
                </TextField>
              </Grid>
            </>
          )}
          <Grid item xs={12} sm={12} md={6} lg={6} className="mt-2">
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Team Leader Photo
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                  Upload a photo of the team leader (Max 1MB)
                </Typography>

                {formData.teamleaderphoto ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <img
                      src={formData.teamleaderphoto}
                      // className="img-responsive"
                      style={{ width: 200, height: 200 }}
                      variant="rounded"
                    />
                    <br />
                    <Box>
                      <Typography variant="body2">
                        Photo uploaded successfully
                      </Typography>
                      <Button
                        color="error"
                        size="small"
                        onClick={handleRemoveTeamLeaderPhoto}
                        sx={{ mt: 1 }}
                      >
                        Remove Photo
                      </Button>
                    </Box>
                  </Box>
                ) : (
                  <></>
                )}
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<CloudUploadIcon />}
                  sx={{ mb: 1 }}
                >
                  Upload Team Leader Photo
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={handleTeamLeaderPhotoUpload}
                  />
                </Button>
                {errors.teamleaderphoto && (
                  <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                    {errors.teamleaderphoto}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        {/* Team Leader Photo Upload */}


        {/* Team Members Section */}
        <Box sx={{ mt: 4, mb: 2 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Team Members ({teamMembers.length}/{MAX_TEAM_MEMBERS})
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Add all team members working on this project
          </Typography>
        </Box>

        {teamMembers.length === 0 ? (
          <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center', py: 4 }}>
            Loading team members...
          </Typography>
        ) : (
          <TableContainer component={Paper} sx={{ mb: 3 }}>
            <Table sx={{ minWidth: 650 }} aria-label="Team members table">
              <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                <TableRow>
                  <TableCell width="5%">#</TableCell>
                  <TableCell width="30%">Team Member Name *</TableCell>
                  <TableCell width="35%">Email Address *</TableCell>
                  <TableCell width="25%">Role *</TableCell>
                  <TableCell width="5%">
                    <Button
                      variant="outlined"
                      startIcon={<AddBoxIcon />}
                      onClick={handleAddMember}
                      disabled={!canAddMore}
                      sx={{ mb: 2 }}
                    >
                      Add
                    </Button>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {teamMembers.map((member, index) => (
                  <TableRow key={index}>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>
                      <TextField
                        variant="outlined"
                        size="small"
                        fullWidth
                        required
                        value={member.team_members_name || ''}
                        onChange={(e) => handleMemberChange(index, 'team_members_name', e.target.value)}
                        error={!!errors?.[`team_members_${index}_name`]}
                        helperText={errors?.[`team_members_${index}_name`]}
                        placeholder="Enter full name"
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        variant="outlined"
                        size="small"
                        fullWidth
                        required
                        type="email"
                        value={member.team_members_email || ''}
                        onChange={(e) => handleMemberChange(index, 'team_members_email', e.target.value)}
                        error={!!errors?.[`team_members_${index}_email`]}
                        helperText={errors?.[`team_members_${index}_email`]}
                        placeholder="email@example.com"
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        variant="outlined"
                        size="small"
                        fullWidth
                        required
                        type="text"
                        value={member.team_members_role || ''}
                        onChange={(e) => handleMemberChange(index, 'team_members_role', e.target.value)}
                        error={!!errors?.[`team_members_${index}_role`]}
                        helperText={errors?.[`team_members_${index}_role`]}
                        placeholder="Role"
                      />
                    </TableCell>
                    <TableCell>
                      {teamMembers.length > 1 && (
                        <IconButton
                          onClick={() => handleRemoveMember(index)}
                          color="error"
                          size="small"
                        >
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Validation summary */}
        {errors?.team_members_common && (
          <Typography color="error" variant="body2" sx={{ mt: 1 }}>
            {errors.team_members_common}
          </Typography>
        )}
      </form>
    </>
  );
};

export default TeamInfo;